import React, { useState, useEffect, useRef, useMemo } from 'react';
import { PackageCheck, Plus, Package, Edit2, Save, X, Trash2, Printer, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { getClientAssignedGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { createAlistamiento } from '../services/alistamientos.services';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import { fetchEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';

// Componente de Input con estado local para evitar pérdida de caracteres
const CantidadInput = ({ value, onChange, onFocus }) => {
  const [localValue, setLocalValue] = useState(value === 0 ? '' : String(value));
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value === 0 ? '' : String(value));
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;

    // Solo permitir números o vacío
    if (newValue === '' || /^\d+$/.test(newValue)) {
      setLocalValue(newValue);

      // Limpiar timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Actualizar el estado padre después de un pequeño delay
      timeoutRef.current = setTimeout(() => {
        onChange(newValue);
      }, 300);
    }
  };

  const handleBlur = () => {
    // Al perder el foco, actualizar inmediatamente
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onChange(localValue);
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      className="h-10 text-center text-lg font-semibold"
      placeholder="0"
    />
  );
};

const NuevoAlistamientoModal = ({ isOpen, onClose, cliente, zona, alistador, lavanderiaId, alistamiento, onSuccess }) => {
  const [prendasDisponibles, setPrendasDisponibles] = useState([]);
  const [bolsas, setBolsas] = useState([]);
  const [bolsaActual, setBolsaActual] = useState({ numero: 1, items: [] });
  const [modoEdicion, setModoEdicion] = useState(false); // Controla si hay una bolsa en edición
  const [observaciones, setObservaciones] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(null); // ID de la bolsa que se está imprimiendo
  const [empleadosDisponibles, setEmpleadosDisponibles] = useState([]);
  const [alistadorSeleccionado, setAlistadorSeleccionado] = useState(null);
  const [alistamientoId, setAlistamientoId] = useState(null); // ID del alistamiento que se está editando
  const { toast } = useToast();

  // Ref para rastrear el alistamiento anterior
  const alistamientoAnteriorRef = useRef(null);

  // Instancia de la impresora (singleton)
  const printer = getPrinterInstance();

  // Calcula el siguiente número de bolsa de forma segura.
  // Usar max(numero)+1 en lugar de length+1 evita colisiones cuando se borra
  // o edita una bolsa intermedia (que dejaría la numeración no contigua).
  const getSiguienteNumeroBolsa = (lista) =>
    lista.length > 0 ? Math.max(...lista.map(b => b.numero)) + 1 : 1;

  useEffect(() => {
    if (isOpen && cliente) {

      // Detectar si cambió de un alistamiento existente a uno nuevo
      const alistamientoIdActual = alistamiento?._id || null;
      const alistamientoIdAnterior = alistamientoAnteriorRef.current?._id || null;
      const cambioDeAlistamiento = alistamientoIdAnterior !== alistamientoIdActual;


      // IMPORTANTE: SIEMPRE limpiar estado primero
      // Esto garantiza que no persistan datos del alistamiento anterior
      setBolsas([]);
      setBolsaActual({ numero: 1, items: [] });
      setObservaciones('');
      setAlistamientoId(null);
      setModoEdicion(false);
      setAlistadorSeleccionado(null);
      setEmpleadosDisponibles([]);

      // Actualizar ref con el alistamiento actual
      alistamientoAnteriorRef.current = alistamiento;

      loadPrendasCliente();

      // Si hay un alistamiento existente, cargar sus datos
      if (alistamiento) {
        // Reconstruir bolsas desde el alistamiento
        const bolsasExistentes = alistamiento.bolsas || [];
        const itemsPorBolsa = {};

        // Agrupar items por bolsa
        (alistamiento.items || []).forEach(item => {
          if (!itemsPorBolsa[item.bolsa]) {
            itemsPorBolsa[item.bolsa] = [];
          }
          itemsPorBolsa[item.bolsa].push({
            prendaId: item.prendaId,
            nombre: item.nombre,
            peso: item.peso,
            cantidad: item.cantidad,
          });
        });


        // Crear array de bolsas.
        // Se deduplica por numero para no arrastrar bolsas repetidas de datos
        // previos corruptos (a la primera ocurrencia se le asignan los items).
        const numerosVistos = new Set();
        const bolsasReconstruidas = bolsasExistentes
          .filter(bolsa => {
            if (numerosVistos.has(bolsa.numero)) return false;
            numerosVistos.add(bolsa.numero);
            return true;
          })
          .map(bolsa => ({
            numero: bolsa.numero,
            identificador: bolsa.identificador,
            items: itemsPorBolsa[bolsa.numero] || [],
            totalItems: bolsa.totalItems,
            pesoTotal: bolsa.pesoTotal,
          }));

        setBolsas(bolsasReconstruidas);
        setBolsaActual({ numero: getSiguienteNumeroBolsa(bolsasReconstruidas), items: [] });
        setObservaciones(alistamiento.observaciones || '');
        setAlistamientoId(alistamiento._id); // Guardar ID del alistamiento en edición
        setModoEdicion(false);
      }
    } else if (!isOpen) {
      // Limpiar estado cuando se cierra el modal
      setBolsas([]);
      setBolsaActual({ numero: 1, items: [] });
      setObservaciones('');
      setAlistamientoId(null); // Limpiar ID del alistamiento
      setModoEdicion(false);
      setAlistadorSeleccionado(null);
      setEmpleadosDisponibles([]);
      alistamientoAnteriorRef.current = null; // Limpiar ref
    }
  }, [isOpen, cliente, alistamiento]);

  const loadPrendasCliente = async () => {
    if (!cliente || !cliente._id) {
      console.error('Cliente sin _id:', cliente);
      toast({
        title: 'Error',
        description: 'No se pudo identificar el cliente',
        variant: 'destructive',
      });
      setPrendasDisponibles([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getClientAssignedGarments(cliente._id);
      setPrendasDisponibles(response.data || []);
    } catch (error) {
      console.error('Error al cargar prendas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas del cliente',
        variant: 'destructive',
      });
      setPrendasDisponibles([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrar prendas según la zona de alistamiento
  const prendasFiltradas = useMemo(() => {
    const zonaActual = alistamiento?.zona || zona;
    
    // Mapear zona a areaAlistamiento
    // "Toallas" -> "toalla", "Lenceria" -> "lenceria"
    const areaRequerida = zonaActual === 'Toallas' ? 'toalla' : 'lenceria';
    
    const filtradas = prendasDisponibles.filter(prenda => {
      // Mostrar si el areaAlistamiento coincide con la zona o es 'ambos'
      const coincide = prenda.areaAlistamiento === areaRequerida || prenda.areaAlistamiento === 'ambos';
      
      
      return coincide;
    });
    
    return filtradas;
  }, [prendasDisponibles, zona, alistamiento]);

  const loadEmpleadosLavanderia = async () => {
    try {
      const response = await fetchEmployees({
        limit: 100,
        offset: 1, // offset es el número de página, debe ser >= 1
        query: {
          'lavanderia.id': lavanderiaId,
          estado: 'Activo'
        }
      });
      // Asegurar que siempre sea un array
      const empleados = Array.isArray(response?.data?.data) ? response.data.data :
        Array.isArray(response?.data) ? response.data :
          Array.isArray(response) ? response : [];
      setEmpleadosDisponibles(empleados);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los empleados',
        variant: 'destructive',
      });
      setEmpleadosDisponibles([]);
    }
  };

  const handleCantidadPrendaChange = (prendaId, cantidad) => {
    // Si el input está vacío o es string vacío, eliminar el item
    if (cantidad === '' || cantidad === null || cantidad === undefined) {
      setBolsaActual(prev => ({
        ...prev,
        items: prev.items.filter(item => item.prendaId !== prendaId),
      }));
      return;
    }

    const qty = parseInt(cantidad);

    // Si no es un número válido o es 0, eliminar
    if (isNaN(qty) || qty === 0) {
      setBolsaActual(prev => ({
        ...prev,
        items: prev.items.filter(item => item.prendaId !== prendaId),
      }));
      return;
    }

    // Si es negativo, ignorar
    if (qty < 0) return;

    setBolsaActual(prev => {
      const existeEnBolsa = prev.items.find(item => item.prendaId === prendaId);

      if (existeEnBolsa) {
        // Actualizar cantidad
        return {
          ...prev,
          items: prev.items.map(item =>
            item.prendaId === prendaId ? { ...item, cantidad: qty } : item
          ),
        };
      } else {
        // Agregar nuevo item
        const prenda = prendasFiltradas.find(p => p.prendaId === prendaId);
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              prendaId: prenda.prendaId,
              nombre: prenda.nombre,
              peso: prenda.peso,
              cantidad: qty,
            },
          ],
        };
      }
    });
  };

  const getCantidadPrenda = (prendaId) => {
    const item = bolsaActual.items.find(item => item.prendaId === prendaId);
    const cantidad = item ? item.cantidad : 0;


    return cantidad;
  };


  const handleAgregarNuevaBolsa = () => {

    // Si hay una bolsa en edición con items, advertir
    if (modoEdicion && bolsaActual.items.length > 0) {
      toast({
        title: 'Aviso',
        description: 'Guarda la bolsa actual antes de crear una nueva',
        variant: 'default',
      });
      return;
    }

    // Iniciar nueva bolsa
    const numeroEsperado = getSiguienteNumeroBolsa(bolsas);
    setBolsaActual({ numero: numeroEsperado, items: [] });
    setModoEdicion(true);
  };

  const handleEditarBolsa = (bolsa) => {

    // Si hay una bolsa en edición, advertir
    if (modoEdicion && bolsaActual.items.length > 0) {
      toast({
        title: 'Aviso',
        description: 'Guarda la bolsa actual antes de editar otra',
        variant: 'default',
      });
      return;
    }

    // Cargar bolsa para editar
    const itemsAEditar = [...bolsa.items];

    setBolsaActual({
      numero: bolsa.numero,
      items: itemsAEditar,
    });

    // Eliminar de bolsas guardadas
    setBolsas(bolsas.filter(b => b.numero !== bolsa.numero));
    setModoEdicion(true);
  };

  const handleCancelarBolsa = () => {
    setBolsaActual({ numero: getSiguienteNumeroBolsa(bolsas), items: [] });
    setModoEdicion(false);
  };

  const handleGuardarBolsa = () => {
    if (bolsaActual.items.length === 0) {
      toast({
        title: 'Aviso',
        description: 'La bolsa debe tener al menos una prenda',
        variant: 'default',
      });
      return;
    }

    const itemsInvalidos = bolsaActual.items.filter(item => !item.cantidad || item.cantidad <= 0);
    if (itemsInvalidos.length > 0) {
      toast({
        title: 'Error',
        description: 'Todas las prendas deben tener cantidad mayor a 0',
        variant: 'destructive',
      });
      return;
    }

    // Calcular totales de la bolsa
    const totalItems = bolsaActual.items.reduce((sum, item) => sum + item.cantidad, 0);
    const pesoTotal = bolsaActual.items.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

    // Garantizar que el número de bolsa sea único. Al editar, la bolsa ya fue
    // removida de `bolsas`, por lo que su número se conserva; si por datos
    // previos hubiera colisión, se reasigna al siguiente número disponible.
    let numeroFinal = bolsaActual.numero;
    if (bolsas.some(b => b.numero === numeroFinal)) {
      numeroFinal = getSiguienteNumeroBolsa(bolsas);
    }

    // Generar identificador de bolsa con sufijo de zona
    const zonaActual = alistamiento?.zona || zona;
    const sufijo = zonaActual === 'Toallas' ? 'T' : 'L';
    const identificador = `${numeroFinal}-${sufijo}`;

    const nuevaBolsa = {
      numero: numeroFinal,
      identificador,
      items: bolsaActual.items,
      totalItems,
      pesoTotal,
    };

    const bolsasActualizadas = [...bolsas, nuevaBolsa];
    setBolsas(bolsasActualizadas);
    setBolsaActual({ numero: getSiguienteNumeroBolsa(bolsasActualizadas), items: [] });
    setModoEdicion(false);

  };

  const handleEliminarBolsa = (numeroBolsa) => {
    setBolsas(bolsas.filter(b => b.numero !== numeroBolsa));
  };

  const handleClickFinalizar = async () => {
    // Validar que haya al menos una bolsa
    if (bolsas.length === 0 && bolsaActual.items.length === 0) {
      toast({
        title: 'Error',
        description: 'Debes agregar al menos una bolsa al alistamiento',
        variant: 'destructive',
      });
      return;
    }

    // Si hay items en la bolsa actual, advertir
    if (bolsaActual.items.length > 0) {
      toast({
        title: 'Aviso',
        description: 'Hay items en la bolsa actual. Guarda la bolsa antes de finalizar',
        variant: 'default',
      });
      return;
    }

    // Cargar empleados y mostrar modal de confirmación
    await loadEmpleadosLavanderia();
    // No pre-seleccionar ningún alistador para forzar selección manual
    setAlistadorSeleccionado(null);
    setShowConfirmModal(true);
  };

  const handleConfirmarFinalizar = async () => {
    if (!alistadorSeleccionado) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar un alistador',
        variant: 'destructive',
      });
      return;
    }
    setShowConfirmModal(false);

    // Guardar bolsas antes de que se limpien
    const bolsasParaImprimir = [...bolsas];
    const clienteParaImprimir = { ...cliente };
    const alistadorParaImprimir = { ...alistadorSeleccionado };

    const alistamientoCreado = await handleGuardarAlistamiento(true);

    // Si se guardó exitosamente, imprimir con los datos guardados
    if (alistamientoCreado) {
      handleImprimirAlistamientoCompleto(alistamientoCreado, bolsasParaImprimir, clienteParaImprimir, alistadorParaImprimir);
    }
  };

  const handleImprimirAlistamientoCompleto = async (alistamientoData, bolsasParaImprimir, clienteParaImprimir, alistadorParaImprimir) => {
    if (!printer.isBluetoothAvailable()) {
      toast({
        title: 'No disponible',
        description: 'Tu navegador no soporta Bluetooth',
        variant: 'destructive',
      });
      return;
    }


    try {
      // Si no está conectado, conectar primero
      if (!printer.isConnected()) {
        toast({
          title: 'Conectando...',
          description: 'Selecciona tu impresora Bluetooth',
        });
        await printer.connect();
      }

      toast({
        title: 'Imprimiendo...',
        description: 'Imprimiendo consolidado de bolsas',
      });

      // Imprimir consolidado de bolsas
      const consecutivo = alistamientoData.consecutivo || 'SIN-CONSECUTIVO';

      // Reconstruir items con número de bolsa
      const todosLosItems = [];
      bolsasParaImprimir.forEach(bolsa => {
        bolsa.items.forEach(item => {
          todosLosItems.push({
            ...item,
            bolsa: bolsa.numero,
          });
        });
      });

      await printer.printConsolidadoBolsas(bolsasParaImprimir, clienteParaImprimir, alistadorParaImprimir, consecutivo, todosLosItems);

      // Pequeño delay antes del consolidado de items
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Imprimir el consolidado de items
      await printer.printConsolidadoAlistamiento(alistamientoData);

      toast({
        title: 'Éxito',
        description: 'Consolidados impresos correctamente',
      });

    } catch (error) {
      console.error('Error al imprimir alistamiento:', error);

      let errorMessage = 'No se pudo imprimir el alistamiento';
      if (error.message.includes('User cancelled')) {
        errorMessage = 'Impresión cancelada';
      } else if (error.message.includes('No hay conexión')) {
        errorMessage = 'Impresora desconectada. Intenta de nuevo.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleImprimirBolsa = async (bolsa) => {
    if (!printer.isBluetoothAvailable()) {
      toast({
        title: 'No disponible',
        description: 'Tu navegador no soporta Bluetooth',
        variant: 'destructive',
      });
      return;
    }

    setIsPrinting(bolsa.numero);

    try {
      // Si no está conectado, conectar primero
      if (!printer.isConnected()) {
        toast({
          title: 'Conectando...',
          description: 'Selecciona tu impresora Bluetooth',
        });
        await printer.connect();
      }

      // Imprimir la bolsa con el consecutivo
      const consecutivo = alistamiento?.consecutivo || 'SIN-CONSECUTIVO';
      await printer.printBolsaResumen(bolsa, cliente, alistador, consecutivo);

      toast({
        title: 'Éxito',
        description: `Bolsa #${bolsa.numero} impresa correctamente`,
      });

    } catch (error) {
      console.error('Error al imprimir bolsa:', error);

      let errorMessage = 'No se pudo imprimir la bolsa';
      if (error.message.includes('User cancelled')) {
        errorMessage = 'Impresión cancelada';
      } else if (error.message.includes('No hay conexión')) {
        errorMessage = 'Impresora desconectada. Intenta de nuevo.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsPrinting(null);
    }
  };

  const handleGuardarAlistamiento = async (finalizar = true) => {
    // Si se va a finalizar, validar que haya al menos una bolsa
    if (finalizar && bolsas.length === 0 && bolsaActual.items.length === 0) {
      toast({
        title: 'Error',
        description: 'Debes agregar al menos una bolsa al alistamiento',
        variant: 'destructive',
      });
      return;
    }

    // Si hay items en la bolsa actual, advertir SIEMPRE (incluido "Guardar
    // Progreso"). El payload solo incluye `bolsas`, no `bolsaActual`, por lo que
    // guardar con una bolsa en edición sin guardarla primero la perdería.
    if (bolsaActual.items.length > 0) {
      toast({
        title: 'Aviso',
        description: 'Hay items en la bolsa actual. Guarda la bolsa antes de continuar',
        variant: 'default',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Preparar todos los items con su número de bolsa
      const todosLosItems = [];
      bolsas.forEach(bolsa => {
        bolsa.items.forEach(item => {
          todosLosItems.push({
            ...item,
            bolsa: bolsa.numero,
          });
        });
      });

      const totalItems = todosLosItems.reduce((sum, item) => sum + item.cantidad, 0);
      const pesoTotal = todosLosItems.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

      // Si se está finalizando, DEBE usar el alistador seleccionado
      // Si solo se guarda progreso, usar el alistador original (usuario logueado)
      let alistadorFinal;
      let nombreAlistador;

      if (finalizar) {
        // Al finalizar, usar el alistador seleccionado (ya validado en handleConfirmarFinalizar)
        alistadorFinal = alistadorSeleccionado;
        nombreAlistador = alistadorFinal.nombre && alistadorFinal.apellido
          ? `${alistadorFinal.nombre} ${alistadorFinal.apellido}`
          : alistadorFinal.name || alistadorFinal.nombre || 'Sin nombre';
      } else {
        // Al guardar progreso: preservar el alistador ya guardado en DB si existe,
        // para evitar que la cuenta de lavandería sobreescriba a un empleado real.
        if (alistamiento?.alistador?.id) {
          alistadorFinal = { _id: alistamiento.alistador.id, nombre: alistamiento.alistador.nombre };
          nombreAlistador = alistamiento.alistador.nombre;
        } else {
          // Nuevo alistamiento aún no guardado: usar usuario logueado como referencia temporal
          alistadorFinal = alistador;
          nombreAlistador = alistadorFinal.name || alistadorFinal.nombre || 'Sin nombre';
        }
      }


      // Determinar zona actual
      const zonaActual = alistamiento?.zona || zona;

      const alistamientoData = {
        alistador: {
          id: alistadorFinal._id || alistadorFinal.id,
          nombre: nombreAlistador,
        },
        cliente: {
          id: cliente._id,
          nombre: cliente.nombre,
          direccion: cliente.direccion || '',
        },
        lavanderiaId,
        zona: zonaActual, // Incluir zona en el payload
        items: todosLosItems,
        bolsas: bolsas.map(b => ({
          numero: b.numero,
          identificador: b.identificador, // Incluir identificador con sufijo
          totalItems: b.totalItems,
          pesoTotal: b.pesoTotal,
        })),
        totalItems,
        pesoTotal,
        observaciones,
        estado: finalizar ? 'Alistado' : 'Pendiente', // Estado según si se finaliza o no
      };

      // Si es un alistamiento existente, incluir el ID desde el estado local
      if (alistamientoId) {
        alistamientoData._id = alistamientoId;
      }

      const response = await createAlistamiento(alistamientoData);

      toast({
        title: 'Éxito',
        description: finalizar
          ? 'Alistamiento finalizado correctamente'
          : 'Progreso guardado correctamente',
      });

      // Limpiar estado después de guardar exitosamente
      setBolsas([]);
      setBolsaActual({ numero: 1, items: [] });
      setObservaciones('');
      setAlistamientoId(null); // Limpiar ID del alistamiento
      setModoEdicion(false);
      setAlistadorSeleccionado(null);
      setEmpleadosDisponibles([]);

      onSuccess?.();
      onClose(); // Cerrar el modal después de guardar

      // Retornar el alistamiento creado con el consecutivo
      return response?.data || alistamientoData;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear el alistamiento',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const totalItemsBolsaActual = bolsaActual.items.reduce((sum, item) => sum + item.cantidad, 0);
  const pesoTotalBolsaActual = bolsaActual.items.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <PackageCheck className="h-5 w-5 text-purple-600" />
                Nuevo Alistamiento - {alistamiento?.zona || zona}
              </DialogTitle>
              <DialogDescription>
                Cliente: <strong>{cliente?.nombre}</strong> | Alistador: <strong>{alistador?.name || alistador?.nombre}</strong> | Zona: <strong>{alistamiento?.zona || zona}</strong>
              </DialogDescription>
            </div>
            <Button onClick={handleAgregarNuevaBolsa} className="gap-2 mr-5">
              <Plus className="h-4 w-4" />
              Agregar Bolsa
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Bolsas del Alistamiento</h3>
          </div>

          {/* Lista de Bolsas */}
          {bolsas.length === 0 && !modoEdicion ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No hay bolsas agregadas</p>
                <Button onClick={handleAgregarNuevaBolsa} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Agregar Primera Bolsa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Bolsa en Edición - PRIMERO */}
              {modoEdicion && (
                <Card className="border-2 border-purple-300 bg-purple-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold">Bolsa #{bolsaActual.numero}</h4>
                        <Badge className="bg-purple-100 text-purple-800">En edición</Badge>
                      </div>

                      <div>
                      <Button
                        onClick={handleGuardarBolsa}
                        disabled={bolsaActual.items.length === 0}
                        className="gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Guardar Bolsa
                      </Button>
           {/*            <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelarBolsa}
                        className="h-8 text-gray-500"
                      >
                        <X className="h-4 w-4" />
                      </Button> */}
                      </div>
                    </div>

                    {/* Selector de prendas - PRIMERO */}
                    <div className="mb-4 bg-white p-4 rounded-lg border-2 border-dashed border-purple-200">
                      <Label className="text-sm font-semibold mb-2 block">Agregar Items a la Bolsa</Label>
                      {isLoading ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                        </div>
                      ) : prendasFiltradas.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No hay prendas disponibles para esta zona</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {prendasFiltradas.map((prenda) => {
                            const cantidad = getCantidadPrenda(prenda.prendaId);
                            const tieneItems = cantidad > 0;

                            return (
                              <div
                                key={prenda.prendaId}
                                className={`border-2 rounded-lg p-3 flex items-center gap-3 ${tieneItems
                                    ? 'border-purple-400 bg-purple-50'
                                    : 'border-gray-200 bg-white'
                                  }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{prenda.nombre}</div>
                                  <div className="text-xs text-gray-500">{prenda.peso}g</div>
                                </div>
                                <div className="flex-shrink-0 w-24">
                                  <CantidadInput
                                    value={cantidad}
                                    onChange={(value) => handleCantidadPrendaChange(prenda.prendaId, value)}
                                    onFocus={(e) => e.target.select()}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Totales y botón guardar 
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex gap-3">
                        <Badge variant="secondary">
                          {totalItemsBolsaActual} Prendas
                        </Badge>
                        <Badge variant="secondary">
                          {pesoTotalBolsaActual}g
                        </Badge>
                      </div>
                      <Button
                        onClick={handleGuardarBolsa}
                        disabled={bolsaActual.items.length === 0}
                        className="gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Guardar Bolsa
                      </Button>
                    </div>*/}
                  </CardContent>
                </Card>
              )}

              {/* Bolsas Guardadas - DESPUÉS */}
              {bolsas.map((bolsa) => (
                <Card key={bolsa.numero} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold">Bolsa #{bolsa.identificador || bolsa.numero}</h4>
                        <Badge variant="secondary">{bolsa.items.length} items</Badge>
                        <Badge variant="secondary">{bolsa.pesoTotal}g - {bolsa.pesoTotal / 1000} Kg</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImprimirBolsa(bolsa)}
                          disabled={isPrinting === bolsa.numero}
                          className="h-8 gap-2"
                        >
                          <Printer className="h-4 w-4" />
                          {isPrinting === bolsa.numero ? 'Imprimiendo...' : 'Imprimir'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditarBolsa(bolsa)}
                          className="h-8 gap-2"
                        >
                          <Edit2 className="h-4 w-4" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEliminarBolsa(bolsa.numero)}
                          className="h-8 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {bolsa.items.map((item, idx) => (
                        <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                          <div className="font-medium truncate">{item.nombre}</div>
                          <div className="text-xs text-gray-500">Cantidad: {item.cantidad}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Observaciones */}
          <div>
            <Label htmlFor="observaciones" className="text-sm font-semibold mb-2 block">
              Observaciones
            </Label>
            <Textarea
              id="observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Observaciones adicionales del alistamiento..."
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cerrar
          </Button>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => handleGuardarAlistamiento(false)}
              disabled={isSaving || bolsas.length === 0}
              className="gap-2"
            >
              {isSaving ? 'Guardando...' : 'Guardar Progreso'}
            </Button>
            <Button
              onClick={handleClickFinalizar}
              disabled={isSaving || bolsas.length === 0}
              className="gap-2"
            >
              {isSaving ? 'Finalizando...' : 'Finalizar Alistamiento'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Modal de Confirmación */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Confirmar Finalización</DialogTitle>
            </div>
            <DialogDescription className="text-base pt-2">
              ¿Estás seguro de que deseas finalizar este alistamiento?
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-semibold">⚠️ Importante:</p>
                <p className="text-sm text-amber-700 mt-1">
                  Una vez finalizado, <strong>no podrás volver a editar</strong> este alistamiento.
                  El estado cambiará a "Alistado" y quedará listo para su entrega.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>

          {/* Selector de Alistador */}
          <div className="mt-4">
            <Label htmlFor="alistador-select" className="text-sm font-semibold mb-2 block">
              Seleccionar Alistador *
            </Label>
            <select
              id="alistador-select"
              value={alistadorSeleccionado?._id || alistadorSeleccionado?.id || ''}
              onChange={(e) => {
                const empleado = empleadosDisponibles.find(
                  emp => emp._id === e.target.value || emp.id === e.target.value
                );
                setAlistadorSeleccionado(empleado);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">-- Seleccionar empleado --</option>
              {Array.isArray(empleadosDisponibles) && empleadosDisponibles.map((empleado) => (
                <option key={empleado._id || empleado.id} value={empleado._id || empleado.id}>
                  {empleado.nombre && empleado.apellido
                    ? `${empleado.nombre} ${empleado.apellido}`
                    : empleado.name || empleado.nombre || 'Sin nombre'}
                </option>
              ))}
            </select>
            {empleadosDisponibles.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No hay empleados disponibles en este centro de lavado</p>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarFinalizar}
              disabled={isSaving || !alistadorSeleccionado}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSaving ? 'Finalizando...' : 'Sí, Finalizar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default NuevoAlistamientoModal;
